import { memo, useId, useMemo } from "react";
import { StyleSheet, Text, View } from "react-native";
import Svg, {
  Circle as SvgCircle,
  Defs,
  LinearGradient,
  Path,
  Stop,
} from "react-native-svg";

import { ThemedText } from "@/components/themed-text";
import { IllustratedAvatar } from "@/components/ui/illustrated-avatar";
import { avatarColor, avatarInitials } from "@/lib/avatar";
import {
  avatarBadge,
  BADGE_EDGE_RATIO,
  BADGE_RING_RATIO,
  BADGE_SIZE_RATIO,
} from "@/lib/avatar-badge";
import {
  resolveAvatarConfig,
  type UserAvatarConfig,
} from "@/lib/avatar-config";
import { useAppTheme } from "@/theme/app-theme";

export type AvatarVariant =
  | "default"
  | "compact"
  | "profile"
  | "chat"
  | "nearby";

export type AvatarProps = {
  /** Structured illustrated-avatar config. Preferred source when present. */
  avatarConfig?: UserAvatarConfig | null;
  /** Safe seed retained for compatibility and legacy normalization. */
  seed?: string;
  name?: string;
  size?: number;
  /** Legacy: a vibe emoji shown when no illustrated avatar can be rendered. */
  emoji?: string;
  variant?: AvatarVariant;
  accessibilityLabel?: string;
  /**
   * Colour of the ring that separates the corner badge from the avatar —
   * the surface the avatar sits ON, so the badge reads as a notch cut out of
   * the circle rather than a sticker laid over it. Defaults to the theme
   * surface; screens with their own palette pass theirs.
   */
  badgeRingColor?: string;
  /**
   * Draw the pin/vibe corner badge. On by default, because a pin is meant to
   * be seen. Screens whose corner is already spoken for — the inbox, where it
   * carries live presence — pass false and keep their own indicator.
   */
  showBadge?: boolean;
  /**
   * Draw the Open-to-chat arc around the circle.
   *
   * The SECOND template on the same profile circle, deliberately, rather than
   * a separate "open avatar" component. Openness is a state a person is in, not
   * a different kind of person, so it belongs to the thing that already draws
   * them — which is also what makes it appear in the Active tab for free, so
   * nobody has to open the Open tab to see who is free.
   *
   * A boolean, not the lease itself: a lease is a comparison against a clock,
   * and doing that inside a memoized list row would make the component
   * re-render on a value it cannot see change. The caller owns the clock
   * (`isOpenNow`) and hands down the answer.
   */
  open?: boolean;
};



/**
 * Renders a user's visual identity. Priority:
 *   1. Saved illustrated avatar (`avatarConfig`)
 *   2. Neutral default illustrated avatar when a seed exists
 *   3. Initials / emoji fallback (only if there is no seed to render from)
 *
 * Backward compatible with the original `{ seed, name, size, emoji }` props so
 * existing screens keep working. Memoized for list performance; renders no
 * network requests or image assets.
 */
function AvatarBase({
  avatarConfig,
  seed,
  name,
  size = 48,
  emoji,
  accessibilityLabel,
  badgeRingColor,
  showBadge = true,
  open = false,
}: AvatarProps) {
  const { colors, isDark } = useAppTheme();

  const config = useMemo(() => {
    if (avatarConfig) return resolveAvatarConfig(avatarConfig, seed);
    if (seed) return resolveAvatarConfig(null, seed);
    return null;
  }, [avatarConfig, seed]);

  // Computed once and shared by both templates below, so the two cannot drift.
  const openGlow = isDark
    ? { color: colors.primary, opacity: OPEN_GLOW_DARK_OPACITY }
    : { color: colors.shadow, opacity: OPEN_GLOW_LIGHT_OPACITY };

  const badgeDiameter = Math.round(size * BADGE_SIZE_RATIO);
  const badge = showBadge ? avatarBadge(config, badgeDiameter) : null;

  const a11yProps = accessibilityLabel
    ? { accessible: true, accessibilityRole: "image" as const, accessibilityLabel }
    : { accessibilityElementsHidden: true, importantForAccessibility: "no" as const };

  if (config) {
    return (
      // Two layers: the circular crop holds the illustration, and the badge
      // sits OUTSIDE that crop so it can straddle the rim — mostly clear of
      // the avatar, notching into it, exactly how the presence dot behaves.
      // The crop must stay on the inner view only; putting it here would
      // slice the badge off again.
      <View style={{ width: size, height: size }} {...a11yProps}>
        <View
          style={{
            width: size,
            height: size,
            borderRadius: size / 2,
            overflow: "hidden",
          }}
        >
          <IllustratedAvatar config={config} size={size} />
        </View>

        {open ? (
          <OpenArc
            size={size}
            color={colors.primary}
            glowColor={openGlow.color}
            glowOpacity={openGlow.opacity}
          />
        ) : null}

        {badge ? (
          <View
            style={[
              styles.badge,
              {
                width: badgeDiameter,
                height: badgeDiameter,
                borderRadius: badgeDiameter / 2,
                borderWidth: Math.max(2, Math.round(badgeDiameter * BADGE_RING_RATIO)),
                borderColor: badgeRingColor ?? colors.surface,
                // Negative: the badge hangs off the box so the avatar's rim
                // runs through its middle, the way the presence dot sits.
                right: size * BADGE_EDGE_RATIO,
                bottom: size * BADGE_EDGE_RATIO,
              },
            ]}
          >
            <Text
              // Never scaled by the OS text setting: the badge is a fixed
              // circle, and a grown glyph would spill out of it.
              maxFontSizeMultiplier={1}
              style={{
                fontSize: badge.fontSize,
                lineHeight: badge.fontSize * 1.15,
                textAlign: "center",
                // Android pads text boxes by default, which pushes a glyph
                // off the badge's centre.
                includeFontPadding: false,
              }}
            >
              {badge.text}
            </Text>
          </View>
        ) : null}
      </View>
    );
  }

  // Fallback: initials / emoji chip (no seed and no config available).
  const color = avatarColor(name ?? "opncue");
  const initials = avatarInitials(name ?? "opncue");
  return (
    <View
      style={[
        styles.base,
        {
          width: size,
          height: size,
          borderRadius: size / 2,
          backgroundColor: color,
        },
      ]}
      {...a11yProps}
    >
      <ThemedText
        tone="onTint"
        style={{
          fontSize: emoji ? size * 0.5 : size * 0.36,
          fontWeight: "800",
          color: "#fff",
        }}
      >
        {emoji ?? initials}
      </ThemedText>
      {/* The fallback chip gets the arc too. It is reached whenever there is
          no seed and no config — and an avatar that silently drops the signal
          on one of its two templates is the bug this whole prop exists to
          avoid. */}
      {open ? (
        <OpenArc
          size={size}
          color={colors.primary}
          glowColor={openGlow.color}
          glowOpacity={openGlow.opacity}
        />
      ) : null}
    </View>
  );
}

/**
 * How thick the arc is, as a fraction of the avatar's diameter.
 *
 * At 7.5% a 54 px inbox avatar gets a 4 px ring and a 96 px profile avatar
 * gets 7.2 px. A fixed width does one of those two jobs badly whichever number
 * is chosen.
 *
 * Was 5.5%, which was too thin to read on a dark ground once the fade had
 * taken anything off it.
 */
const OPEN_ARC_RATIO = 0.075;

/**
 * Where the arc runs, in degrees clockwise from 3 o'clock.
 *
 * SVG's own zero and SVG's own direction — y points DOWN, so a positive angle
 * turns clockwise. 90° is the bottom of the circle, 270° the top.
 *
 * Stated as a start and a sweep rather than as a rotated dash pattern. The
 * previous version set `strokeDasharray` on a full circle and rotated it, which
 * meant the arc's endpoints were implied by three numbers interacting and
 * nobody could point at where they were: its comment claimed the gap sat at the
 * bottom-right to clear the corner badge, and the arithmetic actually put it at
 * the upper-right. Harmless, because the one screen that draws the arc turns
 * the badge off — but the comment was load-bearing for anyone adding a second
 * call site. Two angles cannot drift from their own description, and the bubble
 * and the gradient are both positioned FROM them, so all three agree by
 * construction.
 *
 * 6 o'clock to 12 o'clock up the LEFT side: 90° to 270°, exactly half. The two
 * ends are symmetric about 9 o'clock, which is what makes them read as a pair
 * rather than as an arc that stopped wherever it ran out.
 *
 * The whole right-hand side stays empty, which is where the corner badge hangs
 * and where the inbox row puts its presence dot (centre at about 44°). The
 * arc's nearest approach is its own 6 o'clock end, 20.9 px away at the inbox's
 * 54 px against the 11.9 px the dot's radius and the glow's half-width need —
 * a real margin, where the earlier 9-to-3 run cleared the dot by 0.7 px and
 * got away with it only because the arc's opacity happened to reach zero at
 * exactly that end.
 *
 * Exactly 180° is safe here, despite sitting on the SVG large-arc flag's
 * boundary: with the endpoints diametrically opposite, both candidate arcs are
 * the same length, so the flag cannot pick the wrong one and the sweep flag
 * alone decides direction.
 */
const OPEN_ARC_START_DEG = 90;
const OPEN_ARC_SWEEP_DEG = 180;

/**
 * Where the speech bubble sits, independently of where the arc begins.
 *
 * The arc runs THROUGH the bubble rather than out of it. Tying the two
 * together was the earlier mistake: moving the arc to 6-to-12 dragged the
 * bubble down to the bottom of the circle with it, because the bubble was
 * defined as "the arc's start". It is its own place on the rim — upper-left,
 * where a speech bubble belongs — and the arc happens to pass behind it.
 */
const OPEN_BUBBLE_DEG = 205;

/**
 * The glow: the same arc again, underneath, wider and faint.
 *
 * A real blur would be `FeGaussianBlur`, which react-native-svg supports
 * unevenly across the two platforms and pays a filter pass per avatar in a
 * scrolling list. A second stroke is two draw calls of a shape already
 * computed, looks like a halo at these sizes, and cannot render differently on
 * iOS than on Android.
 */
const OPEN_GLOW_RATIO = 2.4;

/**
 * The glow's colour and strength, per theme.
 *
 * On DARK the glow is the arc's own blue: it sits on a near-black ground, so
 * a blue halo reads as light spilling off the arc, which is what a glow is.
 *
 * On LIGHT that same blue does not work, and the reason is arithmetic rather
 * than taste. `#2468D8` at 20% over white composites to `#D3E1F7`; the
 * illustrated avatar's own disc is about `#D9E7FA`. The glow was landing within
 * a couple of points of the thing it was supposed to be glowing against, so
 * there was nothing to see. The blue-grey `colors.shadow` at 28% composites to
 * `#D7DDE6` instead — grey enough to separate from a blue disc, and it reads as
 * a shadow rather than as a second, paler arc.
 */
const OPEN_GLOW_DARK_OPACITY = 0.2;
const OPEN_GLOW_LIGHT_OPACITY = 0.28;

/**
 * The speech bubble's diameter, as a fraction of the avatar's, and how far its
 * centre sits outside the rim as a fraction of its own radius.
 *
 * Pushed out rather than centred on the rim, which is what the corner badge
 * does. The badge is a small glyph and reads fine half-sunk into the
 * illustration; the bubble is bigger, and centred on the rim it covered a
 * quarter of the face at 96 px — checked by rendering it, not by reasoning
 * about it. At 0.45 it overlaps just enough to read as attached.
 */
const OPEN_BUBBLE_RATIO = 0.26;
const OPEN_BUBBLE_OUT_RATIO = 0.45;

/**
 * How far outside the avatar's box the SVG is allowed to draw, as a fraction
 * of its diameter.
 *
 * The bubble straddles the rim and the glow spreads past it, so both need room
 * the avatar's own square does not have. This is the ONE thing about the arc
 * that reaches outside the component: the canvas is inset negatively and is
 * bigger than `size` on every side, which works because overflow is visible by
 * default — but an ancestor with `overflow: 'hidden'` WILL slice the bubble
 * off. Checked for the only screen that passes `open` today: neither the row
 * shell, the row body, nor the avatar wrapper clips.
 */
const OPEN_ARC_PAD_RATIO = 0.2;

function pointOnCircle(
  cx: number,
  cy: number,
  radius: number,
  degrees: number,
): { x: number; y: number } {
  const radians = (degrees * Math.PI) / 180;
  return {
    x: cx + radius * Math.cos(radians),
    y: cy + radius * Math.sin(radians),
  };
}

/**
 * The Open-to-chat mark: an arc that fades along its length, a glow behind it,
 * and a speech bubble at the end it fades into.
 *
 * The arc sits ON the rim rather than outside it, so it costs no layout at any
 * call site and a list row does not have to make room for it. Only the bubble
 * and the glow reach past the edge — see `OPEN_ARC_PAD_RATIO`.
 *
 * The fade is a linear gradient laid along the CHORD between the arc's two
 * endpoints, not across the bounding box. A box-aligned gradient on an arc
 * fades by height rather than by distance travelled, so both ends come out the
 * same brightness; running it endpoint to endpoint means offset 0 is exactly
 * where the arc starts and offset 1 exactly where it stops.
 */
function OpenArc({
  size,
  color,
  glowColor,
  glowOpacity,
}: {
  size: number;
  color: string;
  /** Separate from `color`: on light grounds the arc's blue is invisible as a
   *  glow. See `OPEN_GLOW_DARK_OPACITY`. */
  glowColor: string;
  glowOpacity: number;
}) {
  const pad = size * OPEN_ARC_PAD_RATIO;
  const canvas = size + pad * 2;
  const stroke = Math.max(1.5, size * OPEN_ARC_RATIO);
  const radius = (size - stroke) / 2;
  // The avatar's centre, in the padded canvas's coordinates.
  const cx = canvas / 2;
  const cy = canvas / 2;

  const start = pointOnCircle(cx, cy, radius, OPEN_ARC_START_DEG);
  const end = pointOnCircle(
    cx,
    cy,
    radius,
    OPEN_ARC_START_DEG + OPEN_ARC_SWEEP_DEG,
  );
  const largeArc = OPEN_ARC_SWEEP_DEG > 180 ? 1 : 0;
  const arcPath =
    `M ${start.x} ${start.y} ` +
    `A ${radius} ${radius} 0 ${largeArc} 1 ${end.x} ${end.y}`;

  // The glow is the same arc on a LARGER radius, offset by exactly half the
  // extra stroke width, so its inner edge lands on the arc's inner edge and it
  // spreads outward only. Drawn concentric it spread both ways, and at 96 px
  // the inward half laid a pale crescent across a quarter of the face.
  const glowStroke = stroke * OPEN_GLOW_RATIO;
  const glowRadius = radius + (glowStroke - stroke) / 2;
  const glowStart = pointOnCircle(cx, cy, glowRadius, OPEN_ARC_START_DEG);
  const glowEnd = pointOnCircle(
    cx,
    cy,
    glowRadius,
    OPEN_ARC_START_DEG + OPEN_ARC_SWEEP_DEG,
  );
  const glowPath =
    `M ${glowStart.x} ${glowStart.y} ` +
    `A ${glowRadius} ${glowRadius} 0 ${largeArc} 1 ${glowEnd.x} ${glowEnd.y}`;

  // Unique per INSTANCE. This is the whole reason `useId` is here, and the
  // reason is a bug rather than tidiness.
  //
  // On web these ids land in the shared document, and `url(#id)` resolves to
  // whichever element carries that id FIRST. Keying them by colour — as this
  // did — gives every open avatar on screen the same two ids, which is invalid
  // and, worse, fragile in exactly one direction: unmount the instance whose
  // definitions happened to be first (switch tabs, recycle a list row) and
  // every surviving arc loses its paint server and renders as NOTHING.
  //
  // That is the shape of the report: the arc and the glow vanished on a tab
  // change and came back on a reload, while the bubble stayed. The bubble is a
  // literal `fill={color}` and never referenced an id, so it was the one part
  // that could not break — which is what identified the cause.
  //
  // `useId` returns something like ":r7:", and a colon is not valid inside a
  // `url(#…)` reference, so the punctuation is stripped.
  const uid = useId().replace(/[^a-zA-Z0-9]/g, "");
  const fade = `openArcFade-${uid}`;
  const glowFade = `openArcGlow-${uid}`;

  const bubbleRadius = (size * OPEN_BUBBLE_RATIO) / 2;
  const bubble = pointOnCircle(
    cx,
    cy,
    radius + bubbleRadius * OPEN_BUBBLE_OUT_RATIO,
    OPEN_BUBBLE_DEG,
  );

  const dotRadius = Math.max(0.8, bubbleRadius * 0.17);
  const dotGap = bubbleRadius * 0.52;

  return (
    <Svg
      width={canvas}
      height={canvas}
      style={{ position: "absolute", left: -pad, top: -pad }}
      pointerEvents="none"
    >
      <Defs>
        <LinearGradient
          id={fade}
          x1={start.x}
          y1={start.y}
          x2={end.x}
          y2={end.y}
          gradientUnits="userSpaceOnUse"
        >
          {/* Full strength across the middle, dissolving into both ends.
              Symmetric, which is what makes the arc read as starting and
              ending deliberately rather than as a ring that was cut off.

              TWO earlier shapes are worth not repeating, both found by
              rendering rather than by reasoning:

                * Peaking at offset 0 and falling away from there. The bubble
                  is opaque and sits over the arc, so the brightest stretch was
                  underneath it and everything visible was already fading — a
                  dim navy smear rather than a signal.
                * Peaking AT the bubble, projected onto the gradient's axis.
                  Same defect for the same reason: the bubble covers its own
                  peak. The fix is not to aim the bright point at the bubble
                  but to keep it away from both ENDS.

              The 0.16 shoulders are in gradient space, which is not arc space:
              the gradient runs along the chord, and the arc is horizontal at
              both ends, so it lingers there. 0.16 of the chord is about 45° of
              arc at the bottom and 30° at the top — soft ends, not clipped
              ones. */}
          <Stop offset="0" stopColor={color} stopOpacity="0" />
          <Stop offset="0.16" stopColor={color} stopOpacity="1" />
          <Stop offset="0.84" stopColor={color} stopOpacity="1" />
          <Stop offset="1" stopColor={color} stopOpacity="0" />
        </LinearGradient>

        {/* The same shape in the glow's colour. A second gradient rather than
            one gradient plus a `stroke` override, because the glow has to
            dissolve at the ends exactly as the arc does — a flat-coloured glow
            under a fading arc leaves two visible stubs where the arc has gone
            and the halo has not. */}
        <LinearGradient
          id={glowFade}
          x1={start.x}
          y1={start.y}
          x2={end.x}
          y2={end.y}
          gradientUnits="userSpaceOnUse"
        >
          <Stop offset="0" stopColor={glowColor} stopOpacity="0" />
          <Stop offset="0.16" stopColor={glowColor} stopOpacity="1" />
          <Stop offset="0.84" stopColor={glowColor} stopOpacity="1" />
          <Stop offset="1" stopColor={glowColor} stopOpacity="0" />
        </LinearGradient>
      </Defs>

      <Path
        d={glowPath}
        stroke={`url(#${glowFade})`}
        strokeWidth={glowStroke}
        strokeOpacity={glowOpacity}
        strokeLinecap="round"
        fill="none"
      />
      <Path
        d={arcPath}
        stroke={`url(#${fade})`}
        strokeWidth={stroke}
        strokeLinecap="round"
        fill="none"
      />

      {/* The bubble, just outside the rim at the arc's start. */}
      <SvgCircle cx={bubble.x} cy={bubble.y} r={bubbleRadius} fill={color} />
      {[-dotGap, 0, dotGap].map((offset) => (
        <SvgCircle
          key={offset}
          cx={bubble.x + offset}
          cy={bubble.y}
          r={dotRadius}
          fill="#FFFFFF"
        />
      ))}
    </Svg>
  );
}

export const Avatar = memo(AvatarBase);

const styles = StyleSheet.create({
  base: {
    alignItems: "center",
    justifyContent: "center",
  },
  // Anchored to the bottom-right, hanging off the box (see BADGE_EDGE_RATIO)
  // so the avatar's rim cuts through its middle. No ancestor of an Avatar may
  // clip its overflow, or this gets sliced.
  badge: {
    position: "absolute",
    alignItems: "center",
    justifyContent: "center",
    backgroundColor: "#FFFFFF",
  },
});
